import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { UserPublicProfile } from '@user/PublicProfile';
import { useUserPublicProfiles } from '@user/PublicProfile';
import { Loading } from '@framework/ui/atoms/Loading';
import { classNames, generateDefaultUserIcon } from '@framework/utils';
import type { GroupMemberRolesJSON, GroupId, UserId, WorkspaceId } from 'balus-common';
import { WorkspaceOperation } from '@workspace/domain/WorkspaceOperation';

type Props = {
  groupId: GroupId;
  workspaceId: WorkspaceId;
  groupMemberRoles: GroupMemberRolesJSON;
  selectedUserIds: string[];
  disabled: boolean;
  onChange(userIds: string[]): void;
  memberFilter: (name: string) => boolean;
};

export const MemberAddList: React.FC<Props> = ({
  groupId,
  workspaceId,
  groupMemberRoles,
  selectedUserIds,
  disabled,
  onChange,
  memberFilter,
}: Props) => {
  const { t } = useTranslation();
  const [availableUserIds, setAvailableUserIds] = useState<UserId[]>([]);

  useEffect(() => {
    WorkspaceOperation.getAddableUserIds(groupId, workspaceId).then((users) => {
      setAvailableUserIds(users.map(({ userId }) => userId));
    });
  }, [groupId, workspaceId]);

  const profiles = useUserPublicProfiles(availableUserIds);

  const handleChangeUser = useCallback(
    (userId: string, checked: boolean) => {
      const newUserIds = checked ? [...selectedUserIds, userId] : selectedUserIds.filter((id) => id !== userId);
      onChange(newUserIds);
    },
    [onChange, selectedUserIds]
  );

  if (!profiles) return <Loading />;

  if (profiles.length === 0) {
    return <div className="flex justify-center px-2 py-4">{t('workspace.memberAdd.emptyState')}</div>;
  }

  return (
    <ul className="px-2">
      {profiles.map((profile) => {
        if (!memberFilter(profile.name)) return null;

        return (
          <MemberItem
            key={profile.id}
            profile={profile}
            checked={selectedUserIds.includes(profile.id)}
            disabled={disabled}
            isGuest={groupMemberRoles[profile.id] === 'guest'}
            onChange={handleChangeUser}
          />
        );
      })}
    </ul>
  );
};

type ItemProps = {
  profile: UserPublicProfile;
  checked: boolean;
  disabled: boolean;
  isGuest: boolean;
  onChange(userId: string, checked: boolean): void;
};

const MemberItem: React.FC<ItemProps> = ({ profile, checked, disabled, isGuest, onChange }: ItemProps) => {
  const { t } = useTranslation();
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(profile.id, event.target.checked);
    },
    [onChange, profile.id]
  );

  return (
    <li className="flex flex-row items-center px-3 py-2 hover:bg-gray-200">
      <label className="flex grow cursor-pointer flex-row truncate">
        <input
          type="checkbox"
          className={classNames(
            'form-checkbox mr-1 h-6 w-6 border-brand',
            disabled ? 'cursor-not-allowed bg-gray-300' : 'cursor-pointer'
          )}
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
        />
        <span className="flex size-6 overflow-hidden rounded-full">
          {profile.imageUrl ? (
            <img className="size-full object-cover" src={profile.imageUrl} alt={profile.name} />
          ) : (
            <div className="size-full object-cover">
              {generateDefaultUserIcon({
                id: profile.id,
                name: profile.name,
                isAnonymous: profile.isAnonymous,
                size: 24,
              })}
            </div>
          )}
        </span>
        <span className="flex h-6 grow truncate px-2 leading-6">{profile.name}</span>
        {isGuest && (
          <span className="mx-1 rounded-lg bg-gray-400 px-2 text-xs">{t('workspace.individualPermission.guest')}</span>
        )}
      </label>
    </li>
  );
};
